import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BrandingContext = createContext();

// Convert hex color to HSL format for CSS variables
const hexToHSL = (hex) => {
  if (!hex) return null;
  
  const cleanHex = hex.replace('#', '');
  if (cleanHex.length !== 6) return null;
  
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  if (isNaN(r) || isNaN(g) || isNaN(b)) return null;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
      default:
        h = 0;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

// Apply branding to DOM
const applyBrandingToDOM = (brandingData) => {
  if (!brandingData) return;

  // Apply accent color as CSS variable
  if (brandingData.accent_color) {
    const hsl = hexToHSL(brandingData.accent_color);
    if (hsl) {
      document.documentElement.style.setProperty('--primary', hsl);
      document.documentElement.style.setProperty('--accent', hsl);
      document.documentElement.style.setProperty('--ring', hsl);
    }
  }

  // Update document title
  if (brandingData.site_title || brandingData.name) {
    document.title = brandingData.site_title || brandingData.name;
  }

  // Update theme color meta tag
  if (brandingData.theme_color) {
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', brandingData.theme_color);
    }
  }

  // Update OG meta tags (for client-side navigation, though crawlers need server-side)
  if (brandingData.og_image_url) {
    const ogImageUrl = brandingData.og_image_url.startsWith('http') 
      ? brandingData.og_image_url 
      : `${API_URL}/api/branding/asset/${brandingData.og_image_url}`;
    
    const ogImageMeta = document.querySelector('meta[property="og:image"]');
    if (ogImageMeta) {
      ogImageMeta.setAttribute('content', ogImageUrl);
    }
  }

  // Update favicon
  if (brandingData.favicon_url) {
    const faviconUrl = brandingData.favicon_url.startsWith('http')
      ? brandingData.favicon_url
      : `${API_URL}/api/branding/asset/${brandingData.favicon_url}`;
    
    const faviconLink = document.querySelector('link[rel="icon"]');
    if (faviconLink) {
      faviconLink.href = faviconUrl;
    }
  }

  // Update manifest link to use dynamic endpoint
  const manifestLink = document.querySelector('link[rel="manifest"]');
  if (manifestLink) {
    manifestLink.href = `${API_URL}/api/manifest.json`;
  }
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchBranding = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/branding`);
      setBranding(response.data);
      applyBrandingToDOM(response.data);
    } catch (error) {
      console.error('Failed to fetch branding:', error);
      // Apply defaults
      const defaults = {
        name: 'Padel League',
        accent_color: '#1e3a5f',
        theme_color: '#1e3a5f'
      };
      setBranding(defaults);
      applyBrandingToDOM(defaults);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  const refreshBranding = useCallback(() => {
    fetchBranding();
  }, [fetchBranding]);

  // Helper to get full asset URL
  const getAssetUrl = useCallback((path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API_URL}/api/branding/asset/${path}`;
  }, []);

  return (
    <BrandingContext.Provider value={{ branding, loading, refreshBranding, getAssetUrl }}>
      {children}
    </BrandingContext.Provider>
  );
};
